/**
 * Quick E-shop Integrations Modal - SIMPLIFIED
 * Just select platform → campaign → list → save
 */

import * as React from 'react';
import { useState, useEffect } from 'react';
import { useAuthStore } from '@store/authStore';
import { useDataSourceStore } from '@store/dataSourceStore';
import { api } from '../../services/api';
import { X, Send, RefreshCw, CheckCircle, Zap } from 'lucide-react';

interface QuickIntegrationsModalProps {
  eshop: any;
  isOpen: boolean;
  onClose: () => void;
}

const QuickIntegrationsModal: React.FC<QuickIntegrationsModalProps> = ({
  eshop,
  isOpen,
  onClose,
}) => {
  const { accessToken, user } = useAuthStore();
  const { getSelection } = useDataSourceStore();

  // Resolve companyId robustly:
  // - If a super-admin has selected a company via the selector, use that
  // - Otherwise fall back to the logged-in user's companyId
  const selection = getSelection();
  const companyId = selection?.companyId || user?.companyId;
  const eshopId = eshop?.id || eshop?._id;

  // State
  const [emailPlatforms, setEmailPlatforms] = useState<any[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [saving, setSaving] = useState(false);

  // Selected values
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [pendingCampaignToSelect, setPendingCampaignToSelect] = useState<string | null>(null);

  // Load email platforms on open
  useEffect(() => {
    if (isOpen) {
      loadEmailPlatforms();
    }
  }, [isOpen]);

  // Load lists when platform changes (GetResponse lists = contact lists/campaigns)
  useEffect(() => {
    if (selectedPlatform) {
      loadCampaigns();
    } else {
      setCampaigns([]);
      setSelectedCampaign('');
    }
  }, [selectedPlatform]);

  // Apply pending campaign selection after campaigns are loaded
  useEffect(() => {
    if (campaigns.length > 0 && pendingCampaignToSelect && !selectedCampaign) {
      const found = campaigns.find((c: any) => c.id === pendingCampaignToSelect);
      if (found) {
        setSelectedCampaign(found.id);
        console.log(`✅ Applied pending campaign selection: ${found.name} (${found.id})`);
        setPendingCampaignToSelect(null);
      } else {
        console.log('⚠️ Pending campaign not found in loaded campaigns:', pendingCampaignToSelect, 'Available:', campaigns.map((c: any) => c.id));
      }
    }
  }, [campaigns, pendingCampaignToSelect]);

  const loadEmailPlatforms = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/v1/companies/${companyId}`);
      const dataSources = response.data.data.dataSources || [];

      console.log('📊 All data sources:', dataSources.length);
      console.log('📊 Data sources details:', dataSources.map((ds: any) => ({
        name: ds.name,
        id: ds.id,
        type: ds.type,
        hasApiKey: !!ds.config?.getresponseApiKey,
        isActive: ds.isActive
      })));

      // Filter for email marketing platforms
      const platforms = dataSources.filter(
        (ds: any) => {
          const isGetResponse = ds.type === 'getresponse' || ds.config?.getresponseApiKey;
          const isEmailOctopus = ds.type === 'emailoctopus' || ds.config?.emailoctopusApiKey;
          const isActive = ds.isActive !== false; // Default to true if not set
          
          return (isGetResponse || isEmailOctopus) && isActive;
        }
      );

      console.log(`📧 Found ${platforms.length} email platform(s):`, platforms.map((p: any) => p.name));
      
      setEmailPlatforms(platforms);
      
      // Auto-select if only one platform
      if (platforms.length === 1) {
        setSelectedPlatform(platforms[0].id);
        console.log(`✅ Auto-selected: ${platforms[0].name}`);
      }

      // Fetch existing integration settings from EshopIntegrationSettings collection
      try {
        const settingsResponse = await api.get(
          `/api/v1/companies/${companyId}/eshop-integration?eshopDataSourceId=${eshopId}`
        );
        
        if (settingsResponse.data.success && settingsResponse.data.data) {
          const savedSettings = settingsResponse.data.data;
          console.log('🔍 Found existing integration for this e-shop:', savedSettings);
          
          // IMPORTANT: Set pending campaign FIRST before setting platform
          // This ensures the pending value is ready when loadCampaigns() runs
          if (savedSettings.defaultCampaignId) {
            setPendingCampaignToSelect(savedSettings.defaultCampaignId);
            console.log(`✅ Will prefill campaign/list: ${savedSettings.defaultCampaignId}`);
          }
          
          // Then set platform (which triggers loadCampaigns via useEffect)
          if (savedSettings.getresponseDataSourceId) {
            setSelectedPlatform(savedSettings.getresponseDataSourceId);
            console.log(`✅ Prefilled platform: ${savedSettings.getresponseDataSourceId}`);
          }
        } else {
          console.log('ℹ️ No existing integration found (first time setup)');
        }
      } catch (settingsError) {
        console.log('ℹ️ No existing integration found or error fetching:', settingsError);
      }
    } catch (error) {
      console.error('❌ Error loading platforms:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      setLoadingCampaigns(true);
      // Fetch contact lists (not email newsletters) from backend
      const response = await api.get('/api/v1/getresponse/lists', {
        params: {
          companyId,
          dataSourceId: selectedPlatform,
        },
      });

      if (response.data.success) {
        // Backend returns lists under `data.lists`
        const rawLists = response.data.data.lists || [];
        console.log(`📋 Loaded ${rawLists.length} lists`, rawLists.slice(0,5));

        // Normalize lists for frontend usage
        const normalized = rawLists.map((l: any) => ({
          id: l.campaignId || l.id,
          name: l.name || '(untitled)',
          raw: l,
        }));

        setCampaigns(normalized);
        console.log(`🔁 Normalized lists:`, normalized.slice(0,5));

        // Auto-select if only one list
        if (normalized.length === 1) {
          setSelectedCampaign(normalized[0].id);
          console.log(`✅ Auto-selected list: ${normalized[0].name}`);
        }
        
        // If we had a pending campaign to select (from existing integration), apply it now
        if (pendingCampaignToSelect) {
          const found = normalized.find((n: any) => n.id === pendingCampaignToSelect);
          if (found) {
            setSelectedCampaign(found.id);
            console.log(`✅ Prefilled campaign from existing integration: ${found.name}`);
            setPendingCampaignToSelect(null);
          } else {
            console.log('⚠️ Pending campaign id not found in fetched lists:', pendingCampaignToSelect);
          }
        }
      }
    } catch (error) {
      console.error('Error loading lists:', error);
      setCampaigns([]);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const handleSave = async () => {
    if (!selectedPlatform) {
      alert('⚠️ Please select an email platform');
      return;
    }

    if (!selectedCampaign) {
      alert('⚠️ Please select a campaign/list');
      return;
    }

    try {
      setSaving(true);

      // Save integration configuration
      const response = await api.put(
        `/api/v1/companies/${companyId}/eshop-integration`,
        {
          eshopDataSourceId: eshopId,
          integration: {
            type: 'getresponse',
            enabled: true,
            dataSourceId: selectedPlatform,
            defaultCampaignId: selectedCampaign,
            autoSyncCustomers: true, // Always enabled for instant sync
            autoSyncAbandonedCarts: false,
            autoSyncAbandonedConsultations: false,
            cronSchedule: '0 2 * * *',
          },
        }
      );

      if (response.data.success) {
        alert(
          '✅ Integration configured!\n\n' +
            '🔄 MongoDB watcher will start automatically.\n' +
            '⚡ New/updated customers will sync instantly to GetResponse!\n\n' +
            'Restart analytics-service to activate the watcher.'
        );
        onClose();
      }
    } catch (error: any) {
      console.error('Error saving:', error);
      alert(`❌ Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Send className="w-6 h-6 text-green-600" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Quick Integration Setup
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Info Banner */}
            <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Zap className="w-5 h-5 text-indigo-600 mt-0.5" />
                <div className="text-sm text-indigo-900 dark:text-indigo-100">
                  <strong>Instant Sync:</strong> Customers will automatically sync to GetResponse
                  within 1-2 seconds when they login, signup, or update their cart!
                </div>
              </div>
            </div>

            {/* Step 1: Select Email Platform */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                1️⃣ Select Email Platform
              </label>
              {emailPlatforms.length > 0 ? (
                <select
                  value={selectedPlatform}
                  onChange={e => {
                    setSelectedPlatform(e.target.value);
                    setSelectedCampaign(''); // Reset campaign when platform changes
                  }}
                  className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 text-base font-medium"
                >
                  <option value="">-- Select Platform --</option>
                  {emailPlatforms.map(platform => (
                    <option key={platform.id} value={platform.id}>
                      📧 {platform.name}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-700 rounded-lg">
                  <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                    ❌ No email platforms found!
                  </p>
                  <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                    Add GetResponse data source in: Companies → Settings → Data Sources
                  </p>
                </div>
              )}
              {emailPlatforms.length > 0 && (
                <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                  ✅ {emailPlatforms.length} platform(s) available
                </p>
              )}
            </div>

            {/* Step 2: Select Campaign/List */}
            {selectedPlatform && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  2️⃣ Select Campaign/List
                </label>
                {loadingCampaigns ? (
                  <div className="flex items-center gap-2 px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                    <RefreshCw className="w-4 h-4 animate-spin text-green-600" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Loading campaigns...
                    </span>
                  </div>
                ) : campaigns.length > 0 ? (
                  <>
                    <select
                      value={selectedCampaign}
                      onChange={e => setSelectedCampaign(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 text-base"
                    >
                      <option value="">-- Select List --</option>
                      {campaigns.map((campaign: any) => (
                        <option key={campaign.id} value={campaign.id}>
                          📋 {campaign.name} {campaign.id ? `(${campaign.id})` : ''}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                      Customers will be automatically added to this list
                    </p>
                  </>
                ) : (
                  <div className="px-4 py-3 bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-200 dark:border-yellow-700 rounded-lg">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      No campaigns found. Create a list in GetResponse first.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Save Button */}
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleSave}
                disabled={!selectedPlatform || !selectedCampaign || saving}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg text-base font-medium"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Save & Enable Instant Sync
                  </>
                )}
              </button>
            </div>

            {/* Info Footer */}
            {selectedPlatform && selectedCampaign && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-3">
                <p className="text-sm text-green-800 dark:text-green-200 font-medium">
                  ⚡ After saving, MongoDB will watch for customer changes and auto-sync within 1-2
                  seconds!
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickIntegrationsModal;

